// @ts-strict-ignore
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { CustomValidators } from '@shared/custom-validators';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { PropertyUtils } from '@article/util/property-utils';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { CoverLabelStripArticleManager } from '@project/components/article/manager/cover-label-strip-article.manager';
import { CoverLabelStripArticleInsertProcessor } from '@project/components/article/processor/prepare/insert/cover-label-strip-article-insert.processor';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { InsertArticleUtils } from '../../article/util/insert-article-utils';
import { InsertArticleService } from '../../article/service/insert-article.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../../article/service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArticleUsageService } from '@article/service/article-usage.service';

@Component({
  standalone: false,
  selector: 'cover-label-strip-settings',
  templateUrl: './cover-label-strip-settings.component.html',
})
export class CoverlabelStripSettingsComponent implements OnInit {
  private readonly VALIDATION_ERROR_MESSAGE_KEY: string = 'settingsTabNavigator.validation.error.cover_label_strip';
  private readonly VALIDATION_ERROR: any = { messageKey: this.VALIDATION_ERROR_MESSAGE_KEY };

  public articleUsage: ArticleUsageVO;

  public form: FormGroup;

  private settingsArticle: ArticleVO;

  constructor(
    private formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private insertArticleUtils: InsertArticleUtils,
    private localeResourceManager: LocaleResourceManager,
    private articleManager: ArticleManager,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private undoRedoHelper: UndoRedoHelper,
    private alertHelper: AlertHelper,
    private undoRedoUtils: UndoRedoUtils,
    private selectionModelService: SelectionModelService,
    private _insertArticleService: InsertArticleService,
    private _applicationRefreshService: ApplicationRefreshService,
    private _detachArticleUsageService: DetachArticleUsageService,
    private articleUsageService: ArticleUsageService
  ) {}

  public ngOnInit(): void {
    const numberValidator = CustomValidators.number(
      (value) => this.localeResourceManager.formatNumber(value),
      this.localeResourceManager.getValueByKey('validation.number.thousands_separator'),
      this.localeResourceManager.getValueByKey('validation.number.decimal_separator'),
      0,
      Number.MAX_VALUE,
      this.VALIDATION_ERROR_MESSAGE_KEY,
      this.VALIDATION_ERROR_MESSAGE_KEY,
      1
    );

    const articleLength: number = UnitConverter.fromToRounded('3d', 'mm', PropertyUtils.getFloatPropertyValue(this.articleUsage.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH));
    const articleOffset: number = UnitConverter.fromToRounded('3d', 'mm', PropertyUtils.getFloatPropertyValue(this.articleUsage.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET));

    this.settingsArticle = this.articleUsageService.getArticleByArticleUsage(this.articleUsage);

    this.form = this.formBuilder.group({
      articleLength: [this.localeResourceManager.formatNumber(articleLength), numberValidator],
      articleOffset: [this.localeResourceManager.formatNumber(articleOffset), numberValidator],
    });
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public async save(): Promise<void> {
    const newLengthValue: number = UnitConverter.fromTo('mm', '3d', this.localeResourceManager.parseNumber(this.form.controls.articleLength.value));
    const newOffsetValue: number = UnitConverter.fromTo('mm', '3d', this.localeResourceManager.parseNumber(this.form.controls.articleOffset.value));

    const maxDynamicArticleLength: number = ArticleUtils.getArticleBehaviorPropertyNumberValue(
      this.settingsArticle,
      ArticleBehaviorPropertyDef.MAX_LENGTH
    );

    const extremeValuesValid: boolean =
      maxDynamicArticleLength >= newLengthValue && newLengthValue >= CoverLabelStripArticleInsertProcessor.MIN_ARTICLE_LENGTH;

    const insertHelper: InsertArticleHelper = this.insertArticleHelperFactory.createInstanceByConnectedArticleUsage(this.articleUsage);
    if (insertHelper && extremeValuesValid && insertHelper.isRelocationAvailable(this.articleUsage.id, newOffsetValue, newLengthValue)) {
      const coverLabelStripArticleManager: CoverLabelStripArticleManager = new CoverLabelStripArticleManager(
        this.projectModel,
        this.projectArticleService,
        this.undoRedoHelper,
        this.alertHelper,
        this.undoRedoUtils,
        this.insertArticleUtils,
        this.selectionModelService,
        this.articleManager,
        this._insertArticleService,
        this._applicationRefreshService,
        this._detachArticleUsageService
      );
      await coverLabelStripArticleManager.recreateArticle(this.articleUsage, newLengthValue, newOffsetValue);
      this.close();
    } else {
      this.form.controls.articleLength.markAsTouched();
      this.form.controls.articleOffset.markAsTouched();

      this.form.controls.articleLength.setErrors(this.VALIDATION_ERROR);
      this.form.controls.articleOffset.setErrors(this.VALIDATION_ERROR);

      await this.form.valueChanges.toPromise();
      this.form.controls.articleLength.updateValueAndValidity({emitEvent: false});
      this.form.controls.articleOffset.updateValueAndValidity({emitEvent: false});
    }
  }
}
